import { SocialIcon } from '@change/ds-core-ui';
import { FacebookRegularIcon as FacebookIcon, InstagramIcon, RedditIcon } from '@change/ds-icons';

import type { TranslationKey } from '@change-corgi/core/i18n';

import type { EnrichedReadyToSharePlatformPostV2 } from '#src/app/shared/api/readyToShare';

import { PostPreviewContainer } from '../components/PostPreviewContainer';

import { stepsFactory } from './stepsFactory';
import type { SharingModalFactoryPropsWithResource } from './types';

type ComponentsFactoryType = {
	iconComponent: React.JSX.Element;
	modalTitle: string;
	modalDescription: string;
	postPreviewComponent: React.JSX.Element;
	nextStepsComponent: React.JSX.Element;
	secondaryCTA: string | undefined;
	primaryCTA: string;
};

// Note: The following constants are exported for testing purposes only. They should not be used outside of this file.

type TranslateFn = (key: TranslationKey | string) => string;

export const ICONS = {
	facebook: (translate: TranslateFn) => (
		<SocialIcon
			icon={<FacebookIcon />}
			aria-label={translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.icons.facebook_aria_label')}
			variant="facebook"
		/>
	),
	instagram: (translate: TranslateFn) => (
		<SocialIcon
			icon={<InstagramIcon />}
			aria-label={translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.icons.instagram_aria_label')}
			variant="instagram"
		/>
	),
	reddit: (translate: TranslateFn) => (
		<SocialIcon
			icon={<RedditIcon />}
			aria-label={translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.icons.reddit_aria_label')}
			variant="reddit"
		/>
	),
};

export const TITLE_COPIES: Record<
	EnrichedReadyToSharePlatformPostV2['__typename'],
	(translate: TranslateFn) => string
> = {
	ReadyToShareFacebookPost: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.titles.facebook_post'),
	ReadyToShareFacebookStory: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.titles.facebook_story'),
	ReadyToShareFacebookGroupsPost: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.titles.facebook_group_post'),
	ReadyToShareInstagramPost: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.titles.instagram_post'),
	ReadyToShareInstagramStory: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.titles.instagram_story'),
	ReadyToShareRedditGroupsPost: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.titles.reddit_group_post'),
};
export const DESCRIPTION_COPIES: Record<
	EnrichedReadyToSharePlatformPostV2['__typename'],
	(translate: TranslateFn) => string
> = {
	ReadyToShareFacebookPost: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.descriptions.facebook_post'),
	ReadyToShareFacebookGroupsPost: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.descriptions.facebook_group_post'),
	ReadyToShareFacebookStory: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.descriptions.facebook_story'),
	ReadyToShareInstagramPost: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.descriptions.instagram_post'),
	ReadyToShareInstagramStory: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.descriptions.instagram_story'),
	ReadyToShareRedditGroupsPost: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.descriptions.reddit_group_post'),
};

type PostPreviewConfig = {
	showDisclaimer: boolean;
	includePetition: boolean;
};

const POST_PREVIEW_CONFIGS: Record<EnrichedReadyToSharePlatformPostV2['__typename'], PostPreviewConfig> = {
	ReadyToShareFacebookPost: { showDisclaimer: true, includePetition: false },
	ReadyToShareFacebookStory: { showDisclaimer: false, includePetition: false },
	ReadyToShareFacebookGroupsPost: { showDisclaimer: true, includePetition: false },
	ReadyToShareInstagramPost: { showDisclaimer: true, includePetition: false },
	ReadyToShareInstagramStory: { showDisclaimer: false, includePetition: false },
	ReadyToShareRedditGroupsPost: { showDisclaimer: true, includePetition: true },
};

function renderPostPreview(
	props: SharingModalFactoryPropsWithResource,
	typename: EnrichedReadyToSharePlatformPostV2['__typename'],
): React.JSX.Element {
	const { petition, recommendedGroup, sharingResource } = props;
	const { showDisclaimer, includePetition } = POST_PREVIEW_CONFIGS[typename];
	return (
		<PostPreviewContainer
			variant={typename}
			previewImageUrl={sharingResource.previewImageUrl}
			message={showDisclaimer ? sharingResource.message : undefined}
			recommendedGroup={recommendedGroup}
			showDisclaimer={showDisclaimer}
			isLoading={false}
			petition={includePetition ? petition : undefined}
		/>
	);
}

export const SECONDARY_CTAS: Record<
	EnrichedReadyToSharePlatformPostV2['__typename'],
	((translate: TranslateFn) => string) | undefined
> = {
	ReadyToShareFacebookPost: undefined,
	ReadyToShareFacebookStory: undefined,
	ReadyToShareFacebookGroupsPost: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.secondary_cta.facebook_group_post'),
	ReadyToShareInstagramPost: undefined,
	ReadyToShareInstagramStory: undefined,
	ReadyToShareRedditGroupsPost: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.secondary_cta.reddit_group_post'),
};
export const PRIMARY_CTAS: Record<
	EnrichedReadyToSharePlatformPostV2['__typename'],
	(translate: TranslateFn) => string
> = {
	ReadyToShareFacebookPost: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.primary_cta.facebook_post'),
	ReadyToShareFacebookStory: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.primary_cta.facebook_story'),
	ReadyToShareFacebookGroupsPost: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.primary_cta.facebook_group_post'),
	ReadyToShareInstagramPost: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.primary_cta.instagram_post'),
	ReadyToShareInstagramStory: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.primary_cta.instagram_story'),
	ReadyToShareRedditGroupsPost: (translate) =>
		translate('corgi.petition.starter_dashboard_v2.shared.sharing_modal.primary_cta.reddit_group_post'),
};

export function componentsFactory(
	props: SharingModalFactoryPropsWithResource,
	translate: TranslateFn,
): ComponentsFactoryType {
	const { post } = props;
	const typename = post.__typename;
	const { platform } = post;

	return {
		iconComponent: ICONS[platform](translate),
		modalTitle: TITLE_COPIES[typename](translate),
		modalDescription: DESCRIPTION_COPIES[typename](translate),
		postPreviewComponent: renderPostPreview(props, typename),
		nextStepsComponent: stepsFactory(typename, translate),
		secondaryCTA: SECONDARY_CTAS[typename]?.(translate),
		primaryCTA: PRIMARY_CTAS[typename](translate),
	};
}
